import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Admin() {
  const { user, impersonate } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [confirm, setConfirm] = useState(null); // user object to confirm delete
  const [editing, setEditing] = useState(null); // user object being edited
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [viewingAsId, setViewingAsId] = useState(null);
  const [showSessions, setShowSessions] = useState(null);
  const [sharingOnly, setSharingOnly] = useState(false);

  const handleViewAs = async (target) => {
    setViewingAsId(target.user_id);
    try {
      await impersonate(target.user_id);
      toast.success(`Viewing as ${target.email}`);
      window.location.href = "/app";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not view as this user");
      setViewingAsId(null);
    }
  };

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
      ]);
      setStats(s.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load");
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setUsers(u.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  const deleteUser = async (u) => {
    setDeletingId(u.user_id);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`Deleted ${u.email}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      setConfirm(null);
      // refresh stats
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setDeletingId(null);
    }
  };

  const saveEdit = async (u, patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.user_id}`, patch);
      setUsers((prev) =>
        prev.map((x) =>
          x.user_id === u.user_id ? { ...x, email: data.email, name: data.name } : x,
        ),
      );
      toast.success(data.updated ? "User updated" : "Nothing to change");
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const bulkDelete = async ({ includeAdmins }) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", {
        include_admins: includeAdmins,
        confirm: includeAdmins ? "WIPE EVERYTHING" : "",
      });
      const removed = data.deleted.users || 0;
      toast.success(`Removed ${removed} account${removed === 1 ? "" : "s"} and all their data`);
      setBulkConfirm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not bulk delete");
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = users.filter((u) => {
    if (sharingOnly && !u.sharing_flagged) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });
  const sharingCount = users.filter((u) => u.sharing_flagged).length;

  return (
    <div className="space-y-6 ff-fade-in" data-testid="admin-page">
      <header>
        <div className="ff-label">Customers</div>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          All customers
        </h1>
        <p className="text-sm text-[#5C6B61] mt-1">
          Search, edit, grant Pro or delete accounts. For analytics see <a href="/owner" className="text-[#3B5A3E] underline">Overview</a>.
        </p>
      </header>

      {loading || !stats ? (
        <div className="text-[#5C6B61]">Loading…</div>
      ) : (
        <>
          <ExtensionAdminCard />
          <StoresAdminCard />
          <section className="ff-card p-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
              <div>
                <div className="ff-label">All users</div>
                <p className="text-sm text-[#5C6B61] mt-1">{users.length} total · click an email to copy</p>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <button
                  onClick={() => setSharingOnly((v) => !v)}
                  data-testid="sharing-filter-btn"
                  title="Show only accounts flagged for impossible-travel sign-ins"
                  className={`rounded-full px-3 py-2 text-xs font-medium transition whitespace-nowrap ${sharingOnly ? "bg-[#A33D26] text-white" : "bg-[#FCF0EC] text-[#A33D26] hover:bg-[#F7CEC1]"}`}
                >
                  🚩 Sharing suspects {sharingCount ? `(${sharingCount})` : ""}
                </button>
                <input
                  className="ff-input sm:w-72"
                  placeholder="Search by name or email…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="user-search"
                />
                <button
                  onClick={() => setBulkConfirm(true)}
                  data-testid="bulk-delete-btn"
                  disabled={users.length <= 1}
                  className="rounded-full px-4 py-2 text-xs font-medium bg-[#FBE5DE] text-[#A33D26] hover:bg-[#F7CEC1] transition disabled:opacity-40 whitespace-nowrap"
                  title="Delete every customer account (optionally including other admins)"
                >
                  Delete accounts…
                </button>
              </div>
            </div>
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[#8B9690] border-b border-[#E5E2DA]">
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">Provider</th>
                    <th className="py-2 pr-3">Joined</th>
                    <th className="py-2 pr-3 text-right">Recipes</th>
                    <th className="py-2 pr-3 text-right">Lists</th>
                    <th className="py-2 pr-3 text-right">Scans</th>
                    <th className="py-2 pr-3 text-center">Pro</th>
                    <th className="py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F3F0EA]">
                  {filtered.map((u) => (
                    <tr key={u.user_id} data-testid="user-row">
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2">
                          <Avatar user={u} />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate flex items-center gap-1.5">
                              {u.sharing_flagged && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FCF0EC] text-[#A33D26] border border-[#E8C4B8] whitespace-nowrap"
                                  title="Logged in from far-apart IPs in a short window — likely account sharing"
                                  data-testid={`sharing-flag-${u.user_id}`}
                                >
                                  🚩 Sharing?
                                </span>
                              )}
                              <span className="truncate">{u.name || "—"}</span>
                            </div>
                            <button
                              onClick={() => {
                                navigator.clipboard?.writeText(u.email || "");
                                toast.success("Email copied");
                              }}
                              className="text-xs text-[#5C6B61] hover:text-[#3B5A3E] truncate text-left"
                            >
                              {u.email}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-3">
                        <ProviderBadge provider={u.auth_provider} />
                      </td>
                      <td className="py-3 pr-3 text-xs text-[#5C6B61] whitespace-nowrap">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="py-3 pr-3 text-right text-[#2A332C]">{u.stats.recipes}</td>
                      <td className="py-3 pr-3 text-right text-[#2A332C]">{u.stats.shopping_lists}</td>
                      <td className="py-3 pr-3 text-right text-[#2A332C]">{u.stats.scans}</td>
                      <td className="py-3 pr-3 text-center">
                        <ProToggle user={u} onChange={(next) => {
                          setUsers((prev) => prev.map((x) => x.user_id === u.user_id ? { ...x, ...next } : x));
                        }} />
                      </td>
                      <td className="py-3 text-right">
                        {u.email === stats.admin_email ? (
                          <span className="ff-chip" style={{ background: "#E8F0E9", color: "#3B5A3E" }}>You</span>
                        ) : (
                          <div className="flex gap-2 justify-end items-center flex-wrap">
                            <button
                              onClick={() => handleViewAs(u)}
                              disabled={viewingAsId === u.user_id}
                              data-testid={`view-as-btn-${u.user_id}`}
                              className="text-xs text-[#3B5A3E] hover:underline disabled:opacity-50 whitespace-nowrap"
                              title={`Sign in as ${u.email} to preview their account`}
                            >
                              {viewingAsId === u.user_id ? "Opening…" : "View as"}
                            </button>
                            <button
                              onClick={() => setEditing(u)}
                              data-testid="edit-user-btn"
                              className="text-xs text-[#3B5A3E] hover:underline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setShowSessions(u)}
                              data-testid={`sessions-btn-${u.user_id}`}
                              className="text-xs text-[#3B5A3E] hover:underline"
                              title="See which device is signed in & force-logout"
                            >
                              Sessions
                            </button>
                            <button
                              onClick={() => setConfirm(u)}
                              disabled={deletingId === u.user_id}
                              data-testid="delete-user-btn"
                              className="text-xs text-[#A33D26] hover:underline disabled:opacity-50"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <p className="text-sm text-[#5C6B61] py-6 text-center">No users match your search.</p>
              )}
            </div>
          </section>

          <RecentlyDeletedPanel onRestored={load} reloadKey={users.length} />
        </>
      )}

      {confirm && (
        <ConfirmModal
          user={confirm}
          deleting={deletingId === confirm.user_id}
          onCancel={() => setConfirm(null)}
          onConfirm={() => deleteUser(confirm)}
        />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          onCancel={() => setEditing(null)}
          onSave={(patch) => saveEdit(editing, patch)}
        />
      )}
      {bulkConfirm && (
        <BulkDeleteModal
          count={users.length - 1}
          busy={bulkBusy}
          onCancel={() => setBulkConfirm(false)}
          onConfirm={bulkDelete}
        />
      )}
      {showSessions && (
        <SessionsModal
          user={showSessions}
          onClose={() => setShowSessions(null)}
        />
      )}
    </div>
  );
}

function BulkDeleteModal({ count, busy, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const required = includeAdmins ? "WIPE EVERYTHING" : "DELETE";
  const ok = typed.trim().toUpperCase() === required;
  return (
    <PortalOverlay onCancel={onCancel} testid="bulk-delete-modal">
      <div className="text-5xl mb-3 text-center">⚠️</div>
      <h2 className="text-2xl font-semibold text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
        {includeAdmins ? "Wipe every account?" : "Delete every customer?"}
      </h2>
      <p className="text-sm text-[#5C6B61] mt-3 text-center">
        {includeAdmins ? (
          <>
            This will permanently remove <strong>every</strong> account
            (including other admins) and ALL of their recipes, shopping lists,
            scans, planners and billing history. <strong>Only your own owner account</strong> is preserved.
          </>
        ) : (
          <>
            This will permanently remove <strong>{count}</strong> customer
            account{count === 1 ? "" : "s"} and ALL of their data. Other admin
            accounts are preserved.
          </>
        )}
      </p>

      <label className="mt-4 flex items-start gap-2.5 p-3 rounded-xl bg-[#FBE5DE]/40 border border-[#F7CEC1] cursor-pointer">
        <input
          type="checkbox"
          checked={includeAdmins}
          onChange={(e) => { setIncludeAdmins(e.target.checked); setTyped(""); }}
          className="w-4 h-4 mt-0.5 accent-[#A33D26]"
          data-testid="bulk-include-admins-checkbox"
        />
        <span className="text-xs text-[#A33D26] leading-snug">
          <strong>Also delete admin accounts</strong> (the demo account and any other admins). Your own account is always kept.
        </span>
      </label>

      <p className="text-sm text-[#5C6B61] mt-4 text-center">
        Type <strong>{required}</strong> to confirm.
      </p>
      <input
        className="ff-input mt-2 text-center"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        data-testid="bulk-delete-confirm-input"
        placeholder={required}
        autoFocus
      />
      <div className="flex gap-2 mt-6 justify-end">
        <button onClick={onCancel} className="ff-btn-ghost border border-[#E5E2DA]" data-testid="bulk-cancel-btn">
          Cancel
        </button>
        <button
          onClick={() => onConfirm({ includeAdmins })}
          disabled={!ok || busy}
          data-testid="bulk-confirm-btn"
          className="rounded-full px-5 py-2.5 text-sm font-medium bg-[#A33D26] text-white hover:bg-[#822E1B] disabled:opacity-40"
        >
          {busy ? "Deleting…" : includeAdmins ? "Wipe everything" : `Delete all ${count}`}
        </button>
      </div>
    </PortalOverlay>
  );
}

// Portals modals to <body> so the fade-in transform on the page doesn't
// trap `position: fixed` (which would otherwise position the modal "halfway
// down the page" relative to the animated container).
function PortalOverlay({ children, onCancel, testid }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-[#2A332C]/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onCancel}
      data-testid={testid}
    >
      <div className="ff-card max-w-md w-full p-6 md:p-8 my-auto" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function EditUserModal({ user, onCancel, onSave }) {
  const [email, setEmail] = useState(user.email || "");
  const [name, setName] = useState(user.name || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({ email: email.trim().toLowerCase(), name: name.trim() });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PortalOverlay onCancel={onCancel} testid="edit-user-modal">
      <form className="space-y-4" onSubmit={submit}>
        <div>
          <div className="ff-label">Editing user</div>
          <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            {user.name || user.email}
          </h2>
        </div>
        <div>
          <label className="ff-label">Display name</label>
          <input
            className="ff-input mt-1"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            data-testid="edit-user-name"
            placeholder="—"
          />
        </div>
        <div>
          <label className="ff-label">Email</label>
          <input
            type="email"
            required
            className="ff-input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="edit-user-email"
          />
          <p className="text-xs text-[#8B9690] mt-1">They&apos;ll sign in with this email next time.</p>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onCancel} className="ff-btn-ghost border border-[#E5E2DA]" data-testid="edit-user-cancel">
            Cancel
          </button>
          <button type="submit" disabled={busy} data-testid="edit-user-save" className="ff-btn-primary disabled:opacity-60">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </PortalOverlay>
  );
}

function SessionsModal({ user, onClose }) {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [maxDevices, setMaxDevices] = useState(3);
  const [busy, setBusy] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  // Per-account device cap override — admins can bump this from 2 → e.g. 4 for
  // a Family Plan account. `null` means "use the global default (2)".
  const [deviceLimitOverride, setDeviceLimitOverride] = useState(user?.device_limit_override ?? null);
  const [pendingLimit, setPendingLimit] = useState(String(user?.device_limit_override ?? ""));
  const [savingLimit, setSavingLimit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${user.user_id}/sessions`);
      setSessions(data.sessions || []);
      if (data.max_devices) setMaxDevices(data.max_devices);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const revokeOne = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      await api.post(`/admin/users/${user.user_id}/sessions/${sessionId}/revoke`);
      toast.success("Device signed out");
      setSessions((s) => s.filter((x) => x.session_id !== sessionId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    } finally {
      setRevokingId(null);
    }
  };

  const revokeAll = async () => {
    if (!window.confirm(`Force-logout every device on ${user.email}? They'll have to sign in again.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.user_id}/sessions/revoke-all`);
      toast.success("All sessions revoked");
      setSessions([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    } finally {
      setBusy(false);
    }
  };

  const saveDeviceLimit = async (explicitValue) => {
    let value;
    // Only accept explicitValue when it's an exact null (reset) or a finite
    // number. Anything else (undefined, event object from a button click, etc.)
    // falls back to reading from the input field.
    if (explicitValue === null || (typeof explicitValue === "number" && Number.isFinite(explicitValue))) {
      value = explicitValue;
    } else {
      const trimmed = (pendingLimit || "").trim();
      value = trimmed === "" ? null : parseInt(trimmed, 10);
    }
    if (value !== null && (!Number.isFinite(value) || value < 1 || value > 50)) {
      toast.error("Device limit must be a whole number between 1 and 50 (or blank to reset).");
      return;
    }
    setSavingLimit(true);
    try {
      const { data } = await api.put(`/admin/users/${user.user_id}/device-limit`, { device_limit: value });
      setDeviceLimitOverride(data.device_limit_override);
      setPendingLimit(data.device_limit_override == null ? "" : String(data.device_limit_override));
      if (data.effective_limit) setMaxDevices(data.effective_limit);
      toast.success(
        value === null
          ? `Device cap reset to default for ${user.email}`
          : `Device cap set to ${value} for ${user.email}`,
        { duration: 6000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update device limit");
    } finally {
      setSavingLimit(false);
    }
  };

  const resetDeviceLimit = () => saveDeviceLimit(null);

  const formatWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return "Just now";
      if (diffMin < 60) return `${diffMin} min ago`;
      if (diffMin < 1440) return `${Math.round(diffMin / 60)} h ago`;
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <PortalOverlay onCancel={onClose} testid="sessions-modal">
      <div>
        <div className="ff-label">Active devices · {sessions.length} of {maxDevices}</div>
        <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          {user.name || user.email}
        </h2>
        <p className="text-xs text-[#8B9690] mt-1">
          The device cap defaults to 2 per email. Raise it for Family Plan accounts or households with several tablets.
        </p>

        <div className="mt-3 p-3 rounded-xl bg-[#F3F0EA] border border-[#E5E2DA]" data-testid="device-limit-editor">
          <div className="ff-label mb-1.5 flex items-center justify-between">
            <span>Custom device cap</span>
            <span className="text-[#5C6B61] font-normal">
              {deviceLimitOverride == null ? "Using default (2)" : `Override: ${deviceLimitOverride}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={50}
              step={1}
              placeholder="Blank = default"
              value={pendingLimit}
              onChange={(e) => setPendingLimit(e.target.value)}
              data-testid="device-limit-input"
              className="ff-input w-32 text-sm"
            />
            <button
              type="button"
              onClick={() => saveDeviceLimit()}
              disabled={savingLimit}
              data-testid="device-limit-save-btn"
              className="text-xs font-semibold rounded-full px-3 py-2 bg-[#3B5A3E] text-white hover:bg-[#2A4230] transition disabled:opacity-60"
            >
              {savingLimit ? "Saving…" : "Save cap"}
            </button>
            {deviceLimitOverride != null && (
              <button
                type="button"
                onClick={resetDeviceLimit}
                disabled={savingLimit}
                data-testid="device-limit-reset-btn"
                className="text-xs text-[#A33D26] hover:underline disabled:opacity-50"
              >
                Reset to default
              </button>
            )}
          </div>
          <p className="text-[11px] text-[#8B9690] mt-2 leading-relaxed">
            The {maxDevices + 1}{[1,21,31].includes(maxDevices+1) ? "st" : [2,22,32].includes(maxDevices+1) ? "nd" : [3,23,33].includes(maxDevices+1) ? "rd" : "th"} login auto-evicts the oldest. Watch for repeated impossible-travel flags or labels rotating between phone &amp; desktop in minutes — that&apos;s the sharing tell.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3" data-testid="sessions-list">
        {loading && <div className="text-sm text-[#8B9690] py-3">Loading sessions…</div>}
        {!loading && sessions.length === 0 && (
          <div className="text-sm text-[#5C6B61] bg-[#FAF6EE] border border-[#E0D5C2] rounded-xl p-4">
            🚪 No active sessions — the user is currently logged out.
          </div>
        )}
        {!loading && sessions.map((s) => (
          <div key={s.session_id || s.fingerprint} className="rounded-xl border border-[#E5E2DA] bg-white p-3" data-testid={`session-${s.session_id || s.fingerprint}`}>
            {s.flagged_for_sharing && s.last_anomaly && (
              <div className="rounded-lg bg-[#FCF0EC] border border-[#E8C4B8] text-[#A33D26] text-xs p-2 mb-2">
                🚩 <strong>Impossible-travel detected.</strong>{" "}
                {(s.last_anomaly.from_geo?.label || "previous location")} → {(s.last_anomaly.to_geo?.label || "this location")} —{" "}
                <strong>{s.last_anomaly.distance_km} km</strong> in {s.last_anomaly.window_hours} h. Likely shared account.
              </div>
            )}
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-none mt-0.5">
                {/(iphone|ipad|android)/i.test(s.device_label || "") ? "📱" : "💻"}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#2A332C]">{s.device_name || s.device_label || "Unknown device"}</div>
                <div className="text-xs text-[#5C6B61] mt-0.5">
                  {s.device_name && s.device_label ? <>{s.device_label} · </> : null}
                  {s.geo?.label ? <>📍 {s.geo.label} · </> : null}
                  IP {s.ip || "—"} · {formatWhen(s.last_seen_at)}
                </div>
                {s.user_agent && (
                  <div className="text-[10px] text-[#8B9690] truncate mt-1" title={s.user_agent}>{s.user_agent}</div>
                )}
              </div>
              <button
                onClick={() => revokeOne(s.session_id)}
                disabled={!s.session_id || revokingId === s.session_id}
                data-testid={`revoke-session-${s.session_id || ""}`}
                className="text-xs text-[#A33D26] hover:underline disabled:opacity-50 whitespace-nowrap flex-none"
                title="Sign this device out of the account"
              >
                {revokingId === s.session_id ? "Revoking…" : "Sign out"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 justify-end pt-5">
        <button onClick={onClose} className="ff-btn-ghost border border-[#E5E2DA]" data-testid="sessions-close">Close</button>
        <button
          onClick={revokeAll}
          disabled={busy || sessions.length === 0}
          data-testid="sessions-revoke-all"
          className="ff-btn-primary disabled:opacity-60"
          style={{ background: "#A33D26" }}
        >
          {busy ? "Revoking…" : "Force-logout"}
        </button>
      </div>
    </PortalOverlay>
  );
}


function ProviderBadge({ provider }) {
  if (provider === "google") {
    return <span className="ff-chip" style={{ background: "#E8F0FB", color: "#1B4E8E" }}>Google</span>;
  }
  return <span className="ff-chip">Email</span>;
}

function Avatar({ user }) {
  if (user.picture) {
    return <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-none" />;
  }
  const letter = (user.name || user.email || "?").slice(0, 1).toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-[#3B5A3E] text-white flex items-center justify-center text-xs font-medium flex-none">
      {letter}
    </div>
  );
}

function ConfirmModal({ user, deleting, onCancel, onConfirm }) {
  return (
    <PortalOverlay onCancel={onCancel} testid="delete-confirm-modal">
      <div className="text-5xl mb-3 text-center">⚠️</div>
      <h2 className="text-2xl font-semibold text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
        Delete this user?
      </h2>
      <p className="text-sm text-[#5C6B61] mt-3 text-center">
        <strong>{user.email}</strong> and all of their recipes, shopping lists, scans and planner data will be removed permanently. This cannot be undone.
      </p>
      <div className="flex gap-2 mt-6 justify-end">
        <button onClick={onCancel} className="ff-btn-ghost border border-[#E5E2DA]" data-testid="cancel-delete-btn">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          data-testid="confirm-delete-btn"
          className="rounded-full px-5 py-2.5 text-sm font-medium bg-[#A33D26] text-white hover:bg-[#822E1B] disabled:opacity-60"
        >
          {deleting ? "Deleting…" : "Delete user"}
        </button>
      </div>
    </PortalOverlay>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

function ProToggle({ user, onChange }) {
  const { user: me, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const btnRef = useRef(null);
  const isPro = !!user.is_pro;
  const expires = user.subscription_expires_at
    ? new Date(user.subscription_expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" })
    : null;
  const isSelf = me?.user_id === user.user_id;

  // Compute the menu position when it opens, and re-compute on resize/scroll
  // so the dropdown follows its trigger even inside a scrolled table.
  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const el = btnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 6,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  const apply = async (days) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/users/${user.user_id}/pro`, { pro: true, days });
      toast.success(`✓ ${user.email} is Pro until ${new Date(data.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`);
      onChange({ is_pro: true, subscription_expires_at: data.expires_at });
      if (isSelf) await refresh();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update Pro");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm(`Revoke Pro from ${user.email}?`)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.user_id}/pro`, { pro: false });
      toast.success("Pro revoked");
      onChange({ is_pro: false, subscription_expires_at: null });
      if (isSelf) await refresh();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-block" data-testid={`pro-toggle-${user.user_id}`}>
      <button
        ref={btnRef}
        onClick={() => setOpen((p) => !p)}
        disabled={busy}
        data-testid={`pro-toggle-btn-${user.user_id}`}
        className={`inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full transition disabled:opacity-50 ${
          isPro
            ? "bg-[#E8F0E9] text-[#3B5A3E] border border-[#cfe0d0] hover:bg-[#d6e6d8]"
            : "bg-white text-[#5C6B61] border border-[#E5E2DA] hover:border-[#3B5A3E]"
        }`}
        title={expires ? `Renews ${expires}` : "Pick a duration to grant Pro"}
      >
        <span>{busy ? "…" : isPro ? `✓ Pro · ${expires}` : "Make Pro"}</span>
        {!busy && (
          <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true" className={`transition-transform ${open ? "rotate-180" : ""}`}>
            <path d="M2 4l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      {open && menuPos && createPortal(
        <>
          <div
            className="fixed inset-0 z-[80] bg-black/20 sm:bg-transparent"
            onClick={() => setOpen(false)}
            data-testid={`pro-menu-backdrop-${user.user_id}`}
          />
          <div
            className="fixed z-[90] w-56 bg-white border border-[#E5E2DA] rounded-xl shadow-2xl p-2 max-w-[calc(100vw-16px)]"
            style={{ top: menuPos.top, right: menuPos.right }}
            data-testid={`pro-menu-${user.user_id}`}
          >
            <div className="text-[10px] uppercase tracking-wide text-[#8B9690] px-2 pt-1 pb-1.5 font-medium">
              {isPro ? "Extend Pro by" : "Grant Pro for"}
            </div>
            {[30, 91, 183, 365].map((d) => (
              <button
                key={d}
                onClick={() => apply(d)}
                data-testid={`pro-grant-${d}-${user.user_id}`}
                className="w-full text-left px-2.5 py-2.5 rounded-lg text-sm hover:bg-[#E8F0E9] text-[#2A332C] font-medium"
              >
                + {d === 30 ? "30 days" : d === 91 ? "3 months" : d === 183 ? "6 months" : "1 year"}
              </button>
            ))}
            {isPro && (
              <>
                <hr className="my-1.5 border-[#F0EDE7]" />
                <button
                  onClick={revoke}
                  data-testid={`pro-revoke-${user.user_id}`}
                  className="w-full text-left px-2.5 py-2 rounded-lg text-xs hover:bg-[#FBE5DE] text-[#A33D26] font-medium"
                >
                  Revoke Pro
                </button>
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}



function RecentlyDeletedPanel({ onRestored, reloadKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [retention, setRetention] = useState(24);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/recently-deleted");
      setItems(data.items || []);
      setRetention(data.retention_hours || 24);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load recently deleted");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/recently-deleted");
        if (cancelled) return;
        setItems(data.items || []);
        setRetention(data.retention_hours || 24);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load recently deleted");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const restore = async (snap) => {
    setBusyId(snap.snapshot_id);
    try {
      await api.post(`/admin/recently-deleted/${snap.snapshot_id}/restore`);
      toast.success(`Restored ${snap.email}`);
      await load();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore");
    } finally {
      setBusyId(null);
    }
  };

  const purge = async (snap) => {
    if (!window.confirm(`Permanently forget ${snap.email}? This cannot be undone.`)) return;
    setBusyId(snap.snapshot_id);
    try {
      await api.delete(`/admin/recently-deleted/${snap.snapshot_id}`);
      toast.success(`Forgotten ${snap.email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not purge");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <section className="ff-card p-5" data-testid="recently-deleted-loading">
        <p className="text-sm text-[#5C6B61]">Loading recently deleted…</p>
      </section>
    );
  }
  if (!items.length) return null;

  return (
    <section className="ff-card p-5" data-testid="recently-deleted-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="ff-label">Recently deleted</div>
          <h3 className="text-base font-semibold mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
            Undo for {retention}h
          </h3>
        </div>
        <button onClick={load} className="text-xs font-medium text-[#5C6B61] hover:text-[#2A332C]" data-testid="recently-deleted-refresh">
          ↻ Refresh
        </button>
      </div>
      <ul className="space-y-2" data-testid="recently-deleted-list">
        {items.map((s) => {
          const deletedAt = s.deleted_at ? new Date(s.deleted_at) : null;
          const expiresAt = deletedAt ? new Date(deletedAt.getTime() + retention * 3600 * 1000) : null;
          const hoursLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / 3600000)) : 0;
          const totals = s.totals || {};
          const totalRows = Object.values(totals).reduce((a, b) => a + (b || 0), 0);
          return (
            <li
              key={s.snapshot_id}
              data-testid={`recently-deleted-row-${s.snapshot_id}`}
              className="flex items-center gap-3 p-3 rounded-xl border border-[#F0EFE9] hover:bg-[#FAF9F6] transition flex-wrap"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#2A332C] truncate">{s.name || "—"}</div>
                <div className="text-xs text-[#5C6B61] truncate">{s.email}</div>
                <div className="text-[11px] text-[#8B9690] mt-1">
                  Deleted {deletedAt?.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })} ·
                  &nbsp;{totalRows} data row{totalRows === 1 ? "" : "s"} kept ·
                  &nbsp;<strong className={hoursLeft <= 2 ? "text-[#A33D26]" : "text-[#3B5A3E]"}>{hoursLeft}h left</strong>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => restore(s)}
                  disabled={busyId === s.snapshot_id}
                  data-testid={`restore-btn-${s.snapshot_id}`}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#E8F0E9] text-[#3B5A3E] hover:bg-[#d6e6d8] transition disabled:opacity-50"
                >
                  {busyId === s.snapshot_id ? "…" : "Restore"}
                </button>
                <button
                  onClick={() => purge(s)}
                  disabled={busyId === s.snapshot_id}
                  data-testid={`purge-btn-${s.snapshot_id}`}
                  className="text-xs font-medium px-3 py-1.5 rounded-full text-[#A33D26] hover:bg-[#FBE5DE] transition disabled:opacity-50"
                >
                  Forget now
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}


// ---------------------------- Admin store config ----------------------------
function StoresAdminCard() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // store object being edited
  const empty = { id: "", name: "", search_url_template: "", package: "", host: "", enabled: true };

  const load = () => {
    setLoading(true);
    api.get("/admin/stores").then(({ data }) => setStores(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load stores"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    try {
      await api.post("/admin/stores", editing);
      toast.success(`Saved ${editing.name}`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(s.source === "default" ? `Disable ${s.name}?` : `Delete ${s.name} permanently?`)) return;
    try {
      await api.delete(`/admin/stores/${s.id}`);
      toast.success(s.source === "default" ? "Disabled" : "Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <section className="ff-card p-6" data-testid="admin-stores-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="ff-label">Supermarket catalogue</div>
          <h2 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>Stores & deep-links</h2>
          <p className="text-sm text-[#5C6B61] mt-1">Edits go live instantly across the web app, extension, and mobile app — no redeploy needed.</p>
        </div>
        <button
          onClick={() => setEditing({ ...empty })}
          className="ff-btn-primary text-sm"
          data-testid="admin-store-add"
        >+ Add store</button>
      </div>

      {loading ? <div className="text-sm text-[#5C6B61]">Loading…</div> : (
        <ul className="divide-y divide-[#E5E2DA]" data-testid="admin-stores-list">
          {stores.map((s) => (
            <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`admin-store-${s.id}`}>
              <span className={`w-2 h-2 rounded-full flex-none ${s.enabled === false ? "bg-[#C36952]" : "bg-[#3B5A3E]"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.name} <span className="text-[10px] text-[#8B9690] uppercase ml-1">{s.source}</span></div>
                <div className="text-xs text-[#8B9690] truncate font-mono">{s.host} · {s.package || "no pkg"}</div>
              </div>
              <button onClick={() => setEditing({ ...s, search_url_template: s.search_url_template })} className="text-xs text-[#3B5A3E] hover:underline">Edit</button>
              <button onClick={() => remove(s)} className="text-xs text-[#C36952] hover:underline">{s.source === "default" ? "Disable" : "Delete"}</button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{stores.some(x => x.id === editing.id) ? "Edit" : "New"} store</h3>
            <div className="space-y-3 mt-4">
              {[
                ["id", "Store ID (lowercase, e.g. tesco)"],
                ["name", "Display name"],
                ["host", "Host (e.g. www.tesco.com)"],
                ["package", "Android package (optional)"],
                ["search_url_template", "Search URL template (use {q} for query)"],
              ].map(([k, label]) => (
                <label key={k} className="block">
                  <span className="ff-label">{label}</span>
                  <input
                    type="text"
                    value={editing[k] || ""}
                    onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                    className="ff-input mt-1 font-mono text-xs"
                    disabled={k === "id" && stores.some(x => x.id === editing.id)}
                    data-testid={`admin-store-field-${k}`}
                  />
                </label>
              ))}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.enabled !== false} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                <span>Enabled</span>
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="flex-1 rounded-full border border-[#E5E2DA] py-2 text-sm">Cancel</button>
              <button onClick={save} className="ff-btn-primary text-sm flex-1" data-testid="admin-store-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}




// ---------------------------- ExtensionAdminCard ----------------------------
function ExtensionAdminCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const downloadUrl = `${apiBase}/api/extension/download`;
  const publicLink = `${(typeof window !== "undefined" ? window.location.origin : "")}/extension`;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/extension/admin/info");
        if (alive) setInfo(data);
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.detail || "Couldn't load extension info");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const copy = async (txt, label) => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };

  const kb = info ? Math.round(info.total_bytes / 1024) : "—";

  return (
    <section className="ff-card p-6" data-testid="admin-extension-card">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-xl flex-none">🥗</div>
        <div className="flex-1 min-w-0">
          <div className="ff-label">Browser extension</div>
          <h2 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            One-click distribution
          </h2>
          {loading ? (
            <p className="text-sm text-[#5C6B61] mt-2">Loading…</p>
          ) : info ? (
            <p className="text-sm text-[#5C6B61] mt-2">
              <strong>v{info.version}</strong> · {info.file_count} files · {kb} KB · <strong>{info.downloads_total}</strong> total downloads. Customers tap the same button on <a className="text-[#3B5A3E] underline" href="/extension" target="_blank" rel="noreferrer">smartershop.xyz/extension</a>.
            </p>
          ) : null}

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <a
              href={downloadUrl}
              download
              className="ff-btn-primary text-sm text-center"
              data-testid="admin-download-ext-btn"
            >
              ⬇ Download zip now
            </a>
            <button
              onClick={() => copy(downloadUrl, "Direct zip URL")}
              className="rounded-full border border-[#3B5A3E] text-[#3B5A3E] hover:bg-[#3B5A3E] hover:text-white text-sm font-medium px-5 py-2.5 transition"
              data-testid="admin-copy-zip-url-btn"
            >
              📋 Copy direct zip URL
            </button>
            <button
              onClick={() => copy(publicLink, "Public extension page")}
              className="rounded-full border border-[#E5E2DA] text-[#5C6B61] hover:border-[#3B5A3E] hover:text-[#3B5A3E] text-sm font-medium px-5 py-2.5 transition"
              data-testid="admin-copy-landing-btn"
            >
              🔗 Copy landing-page URL
            </button>
          </div>

          {info && info.files?.length > 0 && (
            <details className="mt-4">
              <summary className="text-xs text-[#5C6B61] cursor-pointer hover:text-[#3B5A3E]">
                Show files inside the zip ({info.file_count})
              </summary>
              <ul className="mt-2 text-xs text-[#5C6B61] font-mono max-h-40 overflow-y-auto rounded-xl bg-[#F3F0EA] p-3 space-y-0.5">
                {info.files.map((f) => (
                  <li key={f.path} className="flex justify-between gap-2">
                    <span className="truncate">{f.path}</span>
                    <span className="text-[#8B9690] flex-none">{Math.max(1, Math.round(f.bytes / 1024))} KB</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </section>
  );
}
